/**
 * Theme when Agency Gateway embeds this app in an iframe.
 * - Initial: ?gdsTheme=dark|light on the iframe URL
 * - Live: postMessage from parent with { source: 'AgencyGateway', type: 'gds-theme', theme: 'dark'|'light' }
 *
 * GdkDarkModeSwitch mounts after this module runs; it reads persisted preference and wires the GDK
 * button-switch. We mirror Agency Gateway's localStorage key (`darkMode`) and re-run URL sync after
 * GDK init (PrimaryNavigation) so checkbox + switch stay aligned with a real user toggle.
 */
import { isEmbeddedFrame } from "./hooks/useEmbeddedView";

function syncDarkModeLocalStorage(dark) {
    try {
        localStorage.setItem("darkMode", dark ? "true" : "false");
    } catch {
    }
}

export function applyGdsTheme(dark) {
    syncDarkModeLocalStorage(dark);
    if (dark) {
        document.documentElement.setAttribute("data-gdstheme", "dark");
    } else {
        document.documentElement.removeAttribute("data-gdstheme");
    }
    /* GdkDarkModeSwitch toggles `body.dark`; app CSS (e.g. color.css, Sweeps) keys off `.dark`, not only data-gdstheme. */
    document.body?.classList.toggle("dark", dark);
    const cb = document.getElementById("dark-mode-switch-checkbox");
    if (cb) {
        if (cb.checked !== dark) {
            cb.checked = dark;
            cb.dispatchEvent(new Event("input", { bubbles: true }));
            cb.dispatchEvent(new Event("change", { bubbles: true }));
        }
    }
}

export function applyGdsThemeFromUrl() {
    try {
        const t = new URLSearchParams(window.location.search).get("gdsTheme");
        if (t === "dark") applyGdsTheme(true);
        else if (t === "light") applyGdsTheme(false);
    } catch (error) {
        console.debug("Failed to parse gdsTheme from URL:", error);
    }
}

function handleAgencyGatewayParentThemeMessage(event) {
    if (!isEmbeddedFrame()) return;
    if (event.source !== window.parent) return;
    if (!event.origin || !isValidOrigin(event.origin)) return;
    const d = event.data;
    if (d?.source !== "AgencyGateway" || d?.type !== "gds-theme") return;
    if (d.theme === "dark") applyGdsTheme(true);
    else if (d.theme === "light") applyGdsTheme(false);
}

let agencyGatewayParentThemeListenerAttached = false;

export function installAgwParentThemeListener() {
    if (agencyGatewayParentThemeListenerAttached) {
        return;
    }
    window.addEventListener("message", handleAgencyGatewayParentThemeMessage);
    agencyGatewayParentThemeListenerAttached = true;
}

export function removeAgwParentThemeListener() {
    if (!agencyGatewayParentThemeListenerAttached) {
        return;
    }
    window.removeEventListener("message", handleAgencyGatewayParentThemeMessage);
    agencyGatewayParentThemeListenerAttached = false;
}

export function isValidOrigin(origin) {
    if (origin === window.location.origin) return true;

    try {
        const parsed = new URL(origin);
        if (parsed.protocol !== "https:") return false;

        const host = parsed.hostname.toLowerCase();
        return (
            host === "geico.com" ||
            host.endsWith(".geico.com") ||
            host === "geico.net" ||
            host.endsWith(".geico.net")
        );
    } catch {
        return false;
    }
}

applyGdsThemeFromUrl();
installAgwParentThemeListener();
